/**
 * Tabbed contract list, grouped by signing lifecycle.
 *
 * Top-level tabs:
 *  - "Utkast"      → draft, pending, in_review (work-in-progress)
 *  - "I signering" → sent, viewed, awaiting_signature (out for signature)
 *  - "Signerat"    → signed (done)
 *  - "Avbrutet"    → cancelled, archived (only rendered when non-empty)
 *
 * Per-tab refinements:
 *  - status chip strip (only shown when the bucket has >1 distinct status)
 *  - template-type dropdown ("Alla mallar" + each templateKey)
 *
 * Pagination is per-tab: 20 rows per page. Switching tabs resets the page.
 * Rendering of each contract row is delegated to a renderRow prop so callers
 * keep control over which pills/columns to show.
 */

import { useMemo, useState } from 'react';
import type { ContractListRow } from '../api.js';

const PAGE_SIZE = 20;

type Bucket = 'utkast' | 'signering' | 'signerat' | 'avbrutet';

const BUCKET_OF: Record<ContractListRow['status'], Bucket> = {
  draft: 'utkast',
  pending: 'utkast',
  in_review: 'utkast',
  awaiting_manual_signature: 'signering',
  sent: 'signering',
  viewed: 'signering',
  awaiting_signature: 'signering',
  signed: 'signerat',
  archived: 'avbrutet',
  cancelled: 'avbrutet',
};

const BUCKET_LABEL: Record<Bucket, string> = {
  utkast: 'Utkast',
  signering: 'I signering',
  signerat: 'Signerat',
  avbrutet: 'Avbrutet',
};

const STATUS_LABEL: Record<ContractListRow['status'], string> = {
  draft: 'Utkast',
  pending: 'Klar för granskning',
  in_review: 'Granskas',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

type Props = {
  contracts: ContractListRow[];
  renderRow: (c: ContractListRow) => React.ReactNode;
  /** Optional empty-state node when no contracts match the current tab. */
  emptyState?: React.ReactNode;
};

const ALL_TEMPLATES = '__all__';

export function ContractsTabbedList({ contracts, renderRow, emptyState }: Props) {
  // Buckets that have at least one row (Avbrutet hides itself when empty).
  const visibleBuckets = useMemo<Bucket[]>(() => {
    const counts = new Map<Bucket, number>();
    for (const c of contracts) {
      const b = BUCKET_OF[c.status];
      counts.set(b, (counts.get(b) ?? 0) + 1);
    }
    const order: Bucket[] = ['utkast', 'signering', 'signerat', 'avbrutet'];
    return order.filter((b) => b === 'utkast' || (counts.get(b) ?? 0) > 0);
  }, [contracts]);

  const bucketCounts = useMemo(() => {
    const m = new Map<Bucket, number>();
    for (const c of contracts) {
      const b = BUCKET_OF[c.status];
      m.set(b, (m.get(b) ?? 0) + 1);
    }
    return m;
  }, [contracts]);

  const [bucket, setBucket] = useState<Bucket>('utkast');
  const effectiveBucket = visibleBuckets.includes(bucket) ? bucket : 'utkast';

  // Per-bucket: status sub-filter ('' = all) and template-type filter.
  const [statusFilter, setStatusFilter] = useState<ContractListRow['status'] | ''>('');
  const [templateFilter, setTemplateFilter] = useState<string>(ALL_TEMPLATES);
  const [page, setPage] = useState(1);

  // Rows in the active bucket (before secondary filters).
  const inBucket = useMemo(
    () => contracts.filter((c) => BUCKET_OF[c.status] === effectiveBucket),
    [contracts, effectiveBucket],
  );

  // Distinct statuses inside this bucket — used to decide whether to
  // render the chip strip and to enumerate the chips.
  const bucketStatuses = useMemo(() => {
    const set = new Set<ContractListRow['status']>();
    for (const c of inBucket) set.add(c.status);
    return Array.from(set);
  }, [inBucket]);

  const templateOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of inBucket) {
      if (!seen.has(c.templateKey)) seen.set(c.templateKey, c.templateName);
    }
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'sv'));
  }, [inBucket]);

  const filtered = useMemo(() => {
    return inBucket.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (templateFilter !== ALL_TEMPLATES && c.templateKey !== templateFilter) {
        return false;
      }
      return true;
    });
  }, [inBucket, statusFilter, templateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const visible = filtered.slice(pageStart, pageEnd);

  const selectBucket = (b: Bucket) => {
    setBucket(b);
    setStatusFilter('');
    setTemplateFilter(ALL_TEMPLATES);
    setPage(1);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200">
        <div className="flex flex-wrap gap-1">
          {visibleBuckets.map((b) => {
            const active = b === effectiveBucket;
            const count = bucketCounts.get(b) ?? 0;
            return (
              <button
                key={b}
                type="button"
                onClick={() => selectBucket(b)}
                className={`-mb-px border-b-2 px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? 'border-stone-900 text-stone-900'
                    : 'border-transparent text-stone-500 hover:text-stone-800'
                }`}
              >
                {BUCKET_LABEL[b]}
                <span
                  className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] ${
                    active ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {templateOptions.length > 1 && (
          <select
            value={templateFilter}
            onChange={(e) => {
              setTemplateFilter(e.target.value);
              setPage(1);
            }}
            className="mb-1 rounded border border-stone-300 bg-white px-2 py-1 text-xs text-stone-700"
          >
            <option value={ALL_TEMPLATES}>Alla mallar</option>
            {templateOptions.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {bucketStatuses.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          <StatusChip
            label="Alla"
            active={statusFilter === ''}
            onClick={() => {
              setStatusFilter('');
              setPage(1);
            }}
          />
          {bucketStatuses.map((s) => (
            <StatusChip
              key={s}
              label={STATUS_LABEL[s]}
              active={statusFilter === s}
              onClick={() => {
                setStatusFilter(s);
                setPage(1);
              }}
            />
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        emptyState ?? (
          <div className="rounded border border-dashed border-stone-300 p-6 text-center text-sm text-stone-500">
            Inga avtal i den här vyn.
          </div>
        )
      ) : (
        <>
          <ul className="divide-y rounded border">{visible.map((c) => renderRow(c))}</ul>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-stone-500">
              <div>
                <span>{pageStart + 1}</span>–<span>{Math.min(pageEnd, filtered.length)}</span> av{' '}
                <span>{filtered.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="rounded border border-stone-300 px-2 py-1 disabled:opacity-40"
                >
                  Föregående
                </button>
                <span className="px-2">
                  Sida {safePage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="rounded border border-stone-300 px-2 py-1 disabled:opacity-40"
                >
                  Nästa
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatusChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
        active
          ? 'border-stone-900 bg-stone-900 text-white'
          : 'border-stone-300 bg-white text-stone-600 hover:bg-stone-50'
      }`}
    >
      {label}
    </button>
  );
}
